import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";

import FormExtras from "../../../../common/components/formExtras";
import { DashboardLayoutTemplateCreateStore } from "../../store";
import { InputString } from "../../../common/controls";
import { InputCodeEditor } from "../../../common/controls";
import { InputBoolean } from "../../../common/controls";
import { FormControl } from "@mui/material";

export type DashboardLayoutTemplateCreateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  nameSelectedValue?: { displayId: string; id: string } | null;
  onNameChange?: (value: string) => void;
  name: string;
  nameError?: string;
  forRoleSelectedValue?: { displayId: string; id: string } | null;
  onForRoleChange?: (value: string) => void;
  forRole: string;
  forRoleError?: string;
  widgetsSelectedValue?: { displayId: string; id: string } | null;
  onWidgetsChange?: (value: string) => void;
  widgets: string;
  widgetsError?: string;
  isDefaultSelectedValue?: { displayId: string; id: string } | null;
  onIsDefaultChange?: (value: boolean) => void;
  isDefault: boolean;
  isDefaultError?: string;
  enabledSelectedValue?: { displayId: string; id: string } | null;
  onEnabledChange?: (value: boolean) => void;
  enabled: boolean;
  enabledError?: string;
  isForAllTenantsSelectedValue?: { displayId: string; id: string } | null;
  onIsForAllTenantsChange?: (value: boolean) => void;
  isForAllTenants: boolean;
  isForAllTenantsError?: string;
  dashboardLayoutTemplateCreateStore: DashboardLayoutTemplateCreateStore;
};

export const DashboardLayoutTemplateCreateForm: React.FC<DashboardLayoutTemplateCreateFormProps> =
  observer(
    ({
      onSave = () => {},
      onCancel = () => {},
      saving = false,
      showToolbox = true,
      showSaveFooter,
      name = "",
      nameError,
      onNameChange = () => {},
      forRole = "",
      forRoleError,
      onForRoleChange = () => {},
      widgets = "{}",
      widgetsError,
      onWidgetsChange = () => {},
      isDefault = false,
      isDefaultError,
      onIsDefaultChange = () => {},
      enabled = false,
      enabledError,
      onEnabledChange = () => {},
      isForAllTenants = false,
      isForAllTenantsError,
      onIsForAllTenantsChange = () => {},
      dashboardLayoutTemplateCreateStore,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              saveAndCloseLabel={langStore.saveEntity}
              closeLabel={langStore.cancelNewEntity}
              saveForm={() => onSave()}
              saveDisabled={saving}
              closeForm={() => onCancel()}
            />
          )}
          <Box>
            <FormExtras
              entity="DashboardLayoutTemplate"
              mode="create"
              values={{}}
              options={{}}
              onPatch={(campo: string, valor: any) => {
                switch (campo) {
                  case "name":
                    onNameChange?.(valor);
                    break;
                  case "forRole":
                    onForRoleChange?.(valor);
                    break;
                  case "widgets":
                    onWidgetsChange?.(valor);
                    break;
                  case "isDefault":
                    onIsDefaultChange?.(valor);
                    break;
                  case "enabled":
                    onEnabledChange?.(valor);
                    break;
                  case "isForAllTenants":
                    onIsForAllTenantsChange?.(valor);
                    break;
                }
              }}
            />
            <GroupContainer
              id="null"
              errorText={dashboardLayoutTemplateCreateStore.getCardErrors(
                "null"
              )}
              title={langStore.groupDashboardLayoutTemplateLabel}
              collapsible={false}
            >
              <FormControl fullWidth>
                <InputString
                  autoFocus
                  value={name}
                  error={Boolean(nameError)}
                  warning={
                    !nameError &&
                    Boolean(dashboardLayoutTemplateCreateStore.warnings["name"])
                  }
                  helperText={
                    nameError ||
                    dashboardLayoutTemplateCreateStore.warnings["name"]?.message
                  }
                  label={langStore.DashboardLayoutTemplateNameLabel}
                  onChange={(value: string) => onNameChange(value)}
                  description={langStore.DashboardLayoutTemplateNameDescription}
                />

                <InputString
                  value={forRole}
                  error={Boolean(forRoleError)}
                  warning={
                    !forRoleError &&
                    Boolean(
                      dashboardLayoutTemplateCreateStore.warnings["forRole"]
                    )
                  }
                  helperText={
                    forRoleError ||
                    dashboardLayoutTemplateCreateStore.warnings["forRole"]
                      ?.message
                  }
                  label={langStore.DashboardLayoutTemplateForRoleLabel}
                  onChange={(value: string) => onForRoleChange(value)}
                  nullText={langStore.optionalFieldHint}
                  description={
                    langStore.DashboardLayoutTemplateForRoleDescription
                  }
                />

                <InputCodeEditor
                  value={widgets}
                  error={Boolean(widgetsError)}
                  helperText={
                    widgetsError ||
                    dashboardLayoutTemplateCreateStore.warnings["widgets"]
                      ?.message
                  }
                  label={langStore.DashboardLayoutTemplateWidgetsLabel}
                  onChange={(value: string) => onWidgetsChange(value)}
                  language="json"
                  description={
                    langStore.DashboardLayoutTemplateWidgetsDescription
                  }
                />
                <InputBoolean
                  value={isDefault}
                  error={Boolean(isDefaultError)}
                  helperText={
                    isDefaultError ||
                    dashboardLayoutTemplateCreateStore.warnings["isDefault"]
                      ?.message
                  }
                  label={langStore.DashboardLayoutTemplateIsDefaultLabel}
                  description={
                    langStore.DashboardLayoutTemplateIsDefaultDescription
                  }
                  onChange={(value: boolean) => onIsDefaultChange(value)}
                />
                <InputBoolean
                  value={enabled}
                  error={Boolean(enabledError)}
                  helperText={
                    enabledError ||
                    dashboardLayoutTemplateCreateStore.warnings["enabled"]
                      ?.message
                  }
                  label={langStore.DashboardLayoutTemplateEnabledLabel}
                  description={
                    langStore.DashboardLayoutTemplateEnabledDescription
                  }
                  onChange={(value: boolean) => onEnabledChange(value)}
                />
                <InputBoolean
                  value={isForAllTenants}
                  error={Boolean(isForAllTenantsError)}
                  helperText={
                    isForAllTenantsError ||
                    dashboardLayoutTemplateCreateStore.warnings[
                      "isForAllTenants"
                    ]?.message
                  }
                  label={langStore.DashboardLayoutTemplateIsForAllTenantsLabel}
                  onChange={(value: boolean) => onIsForAllTenantsChange(value)}
                />
              </FormControl>
            </GroupContainer>
          </Box>
          {(showSaveFooter ?? !showToolbox) && (
            <FormSaveFooter
              onSave={() => onSave()}
              label={langStore.saveEntity}
              disabled={saving}
            />
          )}
        </Grid>
      );
    }
  );
